import type { ReactNode, SVGAttributes, SVGProps } from 'react';
import styled from 'styled-components';

/**
 * Props every icon in this directory accepts.
 *
 * Anything an `<svg>` takes is forwarded, so an icon can be given a `title`
 * for assistive tech, a `data-*` hook for a test, or be wrapped in
 * `styled(Icon)` by a surface that needs to position it — `className` lands on
 * the element like any other prop.
 */
export interface IconProps extends Omit<
  SVGProps<SVGSVGElement>,
  'width' | 'height' | 'viewBox' | 'children' | 'ref'
> {
  /**
   * Rendered height in px. Width follows the glyph's aspect ratio, so a
   * non-square mark (the folder, the brand "D") never squishes. Defaults to
   * the size the icon was drawn for.
   */
  readonly size?: number;
  /**
   * CSS colour for the glyph. Applied as `color`, and every path paints with
   * `currentColor`, so one value tints the whole mark. Omit to inherit from
   * the surface — which is what most call sites want, since a row's icon
   * should light up with its label rather than needing a second active state.
   */
  readonly color?: string;
  /**
   * Accessible name. Omit for decoration next to a visible label (the common
   * case) and the icon is hidden from assistive tech; pass one when the icon
   * IS the label, and it renders as `role="img"` with an `aria-label`.
   *
   * A glyph whose meaning IS its label everywhere it appears carries the name
   * in its own spec instead (see `IconSpec.title`); this prop overrides that.
   */
  readonly title?: string;
}

/**
 * The one styled `<svg>` behind every icon. Icons live inside flex rows all
 * over the app and must never be the thing that gives — hence `flex-shrink`
 * here rather than repeated in each caller's CSS.
 *
 * `$color` carries the icon's OWN default tint (see `IconSpec.color`) as a
 * class, so a surface can still override it with `styled(Icon)`. A `color`
 * PROP is applied inline instead, so an explicit request always wins.
 */
const IconSvg = styled.svg<{ readonly $color?: string }>`
  flex-shrink: 0;
  ${(p) => (p.$color != null ? `color: ${p.$color};` : '')}
`;

/** The drawing and defaults for one icon. See `createIcon`. */
export interface IconSpec {
  /** Component name, for React DevTools and test output. */
  readonly name: string;
  /** The grid the paths were drawn on, e.g. `'0 0 16 16'`. */
  readonly viewBox: string;
  /** Height in px when the caller doesn't pass `size`. */
  readonly size: number;
  /**
   * Width ÷ height. Defaults to 1 (square). Set it for a glyph whose artwork
   * isn't square, so `size` stays a single number at every call site.
   */
  readonly ratio?: number;
  /** Default tint. Omit to inherit the surrounding text colour. */
  readonly color?: string;
  /**
   * Default accessible name, for a glyph that carries meaning no adjacent text
   * repeats — the pin marking a pinned row, say, where hiding it from
   * assistive tech would drop the only cue that the row is pinned. Leave it
   * unset for the ordinary case: an icon beside its own visible label.
   */
  readonly title?: string;
  /**
   * Attributes merged onto the `<svg>` after the defaults — for a glyph that
   * needs `fill` back, or shared stroke attributes hoisted off its paths.
   */
  readonly svgProps?: SVGAttributes<SVGSVGElement>;
  /** The paths, rects and groups that draw the glyph. */
  readonly children: ReactNode;
}

/** Rounds a derived width to a sane sub-pixel value. */
function px(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Builds an icon component from its drawing.
 *
 * Every icon in this directory is one call to this, which is what keeps them
 * interchangeable: same props, same sizing rule, same a11y behaviour, so a
 * surface can swap one glyph for another without touching anything else.
 */
export function createIcon(spec: IconSpec) {
  function Icon({ size, color, title = spec.title, ...rest }: IconProps) {
    const height = size ?? spec.size;
    const named = title != null && title.length > 0;
    return (
      <IconSvg
        width={px(height * (spec.ratio ?? 1))}
        height={height}
        viewBox={spec.viewBox}
        fill="none"
        focusable="false"
        role={named ? 'img' : undefined}
        aria-label={named ? title : undefined}
        aria-hidden={named ? undefined : true}
        $color={spec.color}
        {...spec.svgProps}
        {...rest}
        style={color != null ? { ...rest.style, color } : rest.style}
      >
        {spec.children}
      </IconSvg>
    );
  }
  Icon.displayName = spec.name;
  return Icon;
}
